import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layers, X, Check, ArrowLeft, ArrowRight, Minimize2, Send, RefreshCw } from "lucide-react";
import { createBulkStyles, BULK_CSS } from "./bulkActions.styles";
import { StepChoose, StepUpload, StepReview, StepSubmit, Btn } from "./BulkSteps";
import { fmt } from "../../Constant/EmployeeActions.bulk.i18n";

const STEPS = ["choose", "upload", "review", "submit"];

/**
 * Bulk Actions wizard (Changes page). All state lives in useBulkEmployeeActions
 * (passed as `bulk`) so the wizard can be minimized and reopened while a batch
 * keeps running.
 */
export default function BulkActionsModal({ bulk, theme, language }) {
  const isRTL = language === "ar";
  const s = useMemo(() => createBulkStyles(theme, isRTL), [theme, isRTL]);
  const { bt, step, setStep } = bulk;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const shellRef = useRef(null);

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirmOpen) setConfirmOpen(false);
      else if (bulk.confirmDiscard) bulk.setConfirmDiscard(false);
      else bulk.closeWizard();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirmOpen, bulk]);

  const idx = STEPS.indexOf(step);
  const Back = isRTL ? ArrowRight : ArrowLeft;
  const Next = isRTL ? ArrowLeft : ArrowRight;
  const running = bulk.batch?.status === "processing";
  const finished = bulk.batch && !running;
  const n = bulk.submittable.length;

  const footer = () => {
    if (step === "choose") {
      return (
        <>
          <div style={s.footerGroup}>
            <Btn s={s} variant="tertiary" onClick={() => bulk.closeWizard()}>{bt.cancel}</Btn>
          </div>
          <div style={s.footerGroup}>
            <Btn s={s} variant="primary" disabled={!bulk.meta || !bulk.selectedTypes.length} onClick={() => setStep("upload")}>
              {bt.next} <Next size={16} />
            </Btn>
          </div>
        </>
      );
    }
    if (step === "upload") {
      const canRetry = bulk.rows.length > 0 && bulk.error && !bulk.busy;
      return (
        <>
          <div style={s.footerGroup}>
            <Btn s={s} variant="secondary" icon={Back} disabled={!!bulk.busy} onClick={() => setStep("choose")}>{bt.back}</Btn>
          </div>
          <div style={s.footerGroup}>
            <Btn s={s} variant="tertiary" onClick={() => bulk.closeWizard()}>{bt.cancel}</Btn>
            {canRetry && (
              <Btn s={s} variant="primary" icon={RefreshCw} onClick={() => bulk.validateRows(bulk.rows)}>{bt.retry}</Btn>
            )}
          </div>
        </>
      );
    }
    if (step === "review") {
      return (
        <>
          <div style={s.footerGroup}>
            <Btn s={s} variant="secondary" icon={Back} disabled={!!bulk.busy} onClick={() => setStep("upload")}>{bt.back}</Btn>
          </div>
          <div style={s.footerGroup}>
            <Btn s={s} variant="tertiary" onClick={() => bulk.closeWizard()}>{bt.cancel}</Btn>
            <Btn s={s} variant="primary" icon={Send} disabled={!n} busy={bulk.busy === "committing"} onClick={() => setConfirmOpen(true)}>
              {n === 1 ? bt.submit1 : fmt(bt.submitN, { n })}
            </Btn>
          </div>
        </>
      );
    }
    // submit
    return running ? (
      <>
        <span />
        <div style={s.footerGroup}>
          <Btn s={s} variant="secondary" icon={Minimize2} onClick={bulk.minimize}>{bt.minimize}</Btn>
        </div>
      </>
    ) : (
      <>
        <div style={s.footerGroup}>
          <Btn s={s} variant="secondary" icon={RefreshCw} onClick={() => { bulk.reset(); }}>{bt.newUpload}</Btn>
        </div>
        <div style={s.footerGroup}>
          <Btn s={s} variant="primary" icon={Check} onClick={() => bulk.closeWizard(true)}>{bt.done}</Btn>
        </div>
      </>
    );
  };

  const c = bulk.confirmSummary;

  return (
    <div className="nxb-overlay" style={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && bulk.closeWizard()}>
      <style>{BULK_CSS}</style>
      <div
        ref={shellRef}
        className="nxb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nxb-title"
        tabIndex={-1}
        style={{ ...s.shell, position: "relative", outline: "none" }}
      >
        {/* Header */}
        <div className="nxb-header" style={s.header}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            <div style={s.headerIcon}><Layers size={24} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="nxb-title" style={s.title}>{bt.title}</h2>
              <p style={s.subtitle} className="nxb-hide-sm">{bt.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="nxb-btn"
            style={s.iconButton}
            aria-label={running ? bt.minimize : bt.close}
            title={running ? bt.minimize : bt.close}
            onClick={() => bulk.closeWizard()}
          >
            {running ? <Minimize2 size={18} /> : <X size={18} />}
          </button>
        </div>

        {/* Stepper */}
        <div className="nxb-stepper" style={s.stepper} aria-label={fmt(bt.stepOf, { n: idx + 1 })}>
          {STEPS.map((key, i) => {
            const state = i < idx || (key === "submit" && finished) ? "done" : i === idx ? "active" : "todo";
            return (
              <React.Fragment key={key}>
                <div style={s.stepItem(state)} aria-current={state === "active" ? "step" : undefined}>
                  <span style={s.stepDot(state)}>{state === "done" ? <Check size={14} strokeWidth={3} /> : i + 1}</span>
                  <span>{bt.steps[key]}</span>
                </div>
                {i < STEPS.length - 1 && <div style={s.stepLine(i < idx)} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="nxb-body" style={s.body}>
          {step === "choose" && (
            <StepChoose
              s={s} bt={bt} isRTL={isRTL}
              meta={bulk.meta} metaError={bulk.metaError} loadMeta={bulk.loadMeta}
              selectedTypes={bulk.selectedTypes} toggleType={bulk.toggleType}
              templateLang={bulk.templateLang} setTemplateLang={bulk.setTemplateLang}
              templateBusy={bulk.templateBusy} downloadTemplate={bulk.downloadTemplate}
              onSkipToUpload={() => setStep("upload")}
            />
          )}
          {step === "upload" && (
            <StepUpload
              s={s} bt={bt} meta={bulk.meta}
              file={bulk.file} rows={bulk.rows} busy={bulk.busy} error={bulk.error}
              handleFile={bulk.handleFile} removeFile={bulk.removeFile}
            />
          )}
          {step === "review" && (
            <>
              <StepReview
                s={s} bt={bt} language={language} isRTL={isRTL}
                validation={bulk.validation} downloadErrorReport={bulk.downloadErrorReport}
              />
              {bulk.error && (
                <div style={s.alert("error")} role="alert">{bulk.error}</div>
              )}
            </>
          )}
          {step === "submit" && (
            <StepSubmit s={s} bt={bt} language={language} batch={bulk.batch} downloadReport={bulk.downloadReport} />
          )}
          {step === "choose" && bulk.error && (
            <div style={s.alert("error")} role="alert">{bulk.error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="nxb-footer" style={s.footer}>{footer()}</div>

        {/* Confirm submit */}
        {confirmOpen && (
          <div style={s.confirmBackdrop} onMouseDown={(e) => e.target === e.currentTarget && setConfirmOpen(false)}>
            <div style={s.confirmCard} role="alertdialog" aria-labelledby="nxb-confirm-title">
              <h3 id="nxb-confirm-title" style={s.sectionTitle}>{fmt(bt.confirmTitle, { n: c.total })}</h3>
              <ul style={s.confirmList}>
                {c.now > 0 && <li style={s.confirmItem}><span style={s.dot("#10b981")} />{fmt(bt.confirmNow, { n: c.now })}</li>}
                {c.scheduled > 0 && <li style={s.confirmItem}><span style={s.dot("#3b82f6")} />{fmt(bt.confirmScheduled, { n: c.scheduled })}</li>}
                {c.approval > 0 && <li style={s.confirmItem}><span style={s.dot("#f59e0b")} />{fmt(bt.confirmApproval, { n: c.approval })}</li>}
                {c.skipped > 0 && <li style={s.confirmItem}><span style={s.dot("#94a3b8")} />{fmt(bt.confirmSkipped, { n: c.skipped })}</li>}
              </ul>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                <Btn s={s} variant="secondary" onClick={() => setConfirmOpen(false)}>{bt.cancel}</Btn>
                <Btn
                  s={s}
                  variant="primary"
                  icon={Send}
                  busy={bulk.busy === "committing"}
                  onClick={async () => { await bulk.submit(); setConfirmOpen(false); }}
                >
                  {bt.confirmYes}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* Discard validated file */}
        {bulk.confirmDiscard && (
          <div style={s.confirmBackdrop} onMouseDown={(e) => e.target === e.currentTarget && bulk.setConfirmDiscard(false)}>
            <div style={s.confirmCard} role="alertdialog" aria-labelledby="nxb-discard-title">
              <h3 id="nxb-discard-title" style={s.sectionTitle}>{bt.discardTitle}</h3>
              <p style={s.sectionHint}>{bt.discardBody}</p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                <Btn s={s} variant="secondary" onClick={() => bulk.setConfirmDiscard(false)}>{bt.keep}</Btn>
                <Btn s={s} variant="danger" onClick={() => bulk.closeWizard(true)}>{bt.discard}</Btn>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
